import type { CtcaeVersion, SearchResult } from "./types";

export const SEARCH_DWELL_MS = 2_000;

type SearchSelection = Pick<SearchResult, "matchType"> & {
  record: Pick<SearchResult["record"], "id" | "code" | "term">;
};

export type SearchEvent = {
  event: "settled" | "selection";
  query: string;
  version: CtcaeVersion;
  selectedAnswer?: {
    id: string;
    code: string;
    term: string;
    matchType: SearchResult["matchType"];
  };
};

type SubmitSearchEvent = (event: SearchEvent) => Promise<void> | void;

export function submitSearchEvent(event: SearchEvent) {
  return fetch("/api/searches", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(event),
    keepalive: true,
  }).then((response) => {
    if (!response.ok) throw new Error(`Search logging failed with status ${response.status}.`);
  });
}

export function createSearchRecorder(
  submit: SubmitSearchEvent = submitSearchEvent,
  dwellMs = SEARCH_DWELL_MS,
) {
  let pending: { query: string; version: CtcaeVersion } | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const cancelPending = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    pending = undefined;
  };

  const send = (event: SearchEvent) => {
    try {
      void Promise.resolve(submit(event)).catch(() => undefined);
    } catch {
      // Search logging is best-effort and must never interrupt the lookup UI.
    }
  };

  const schedule = (rawQuery: string, version: CtcaeVersion) => {
    cancelPending();
    if (!rawQuery.trim()) return;
    pending = { query: rawQuery, version };
    timer = setTimeout(() => {
      const settled = pending;
      timer = undefined;
      pending = undefined;
      if (settled) send({ event: "settled", ...settled });
    }, dwellMs);
  };

  const select = (rawQuery: string, version: CtcaeVersion, selection: SearchSelection) => {
    cancelPending();
    if (!rawQuery.trim()) return;
    send({
      event: "selection",
      query: rawQuery,
      version,
      selectedAnswer: {
        id: selection.record.id,
        code: selection.record.code,
        term: selection.record.term,
        matchType: selection.matchType,
      },
    });
  };

  return { schedule, select, cancel: cancelPending };
}
